import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type Theme = 'light' | 'dark';

type ThemeContextValue = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const storageKey = 'athwab-theme';

const getPreferredTheme = (): Theme => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const stored = window.localStorage.getItem(storageKey) as Theme | null;
  if (stored) {
    return stored;
  }

  return 'dark';
};

const safeApplyTheme = (value: Theme) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  const element = document.documentElement;
  element.classList.remove(value === 'dark' ? 'light' : 'dark');
  element.classList.add(value);
  window.localStorage.setItem(storageKey, value);
};

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const [theme, setThemeState] = useState<Theme>(() => getPreferredTheme());

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    safeApplyTheme(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      safeApplyTheme(next);
      return next;
    });
  }, []);

  useEffect(() => {
    safeApplyTheme(theme);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [setTheme, theme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
